<script lang="ts">
  import { onMount } from 'svelte';
  import { editorStore, currentData } from '../../stores/editorStore';
  import { undoStore } from '../../stores/undoStore';
  import { announceToScreenReader } from '../../lib/accessibility';
  import type { WordBank } from '@blather/shared';

  let overview: any = null;
  let selectedBank: string | null = null;
  let newWord = '';

  onMount(async () => {
    try {
      overview = await editorStore.loadOverview();
      await editorStore.setMode('wordbanks', overview);
    } catch (error) {
      console.error('Failed to load overview:', error);
    }
  });

  $: wordBank = $currentData as WordBank | null;
  $: words = wordBank?.words || [];

  async function selectBank(slotType: string) {
    selectedBank = slotType;
    try {
      await editorStore.selectItem(slotType);
      announceToScreenReader(`Loaded ${slotType} wordbank with ${words.length} words`, 'polite');
    } catch (error) {
      console.error('Failed to load word bank:', error);
    }
  }

  function addWord() {
    if (!newWord.trim() || !selectedBank || !wordBank) return;

    const trimmedWord = newWord.trim();

    // Check for duplicates (case-insensitive)
    if (words.some(w => w.toLowerCase() === trimmedWord.toLowerCase())) {
      announceToScreenReader(`Word "${trimmedWord}" already exists`, 'assertive');
      return;
    }

    const currentBank = wordBank;
    const updatedBank: WordBank = {
      ...currentBank,
      words: [...currentBank.words, trimmedWord],
    };

    undoStore.recordAction({
      type: 'add',
      target: 'wordbank',
      targetKey: selectedBank,
      itemKey: trimmedWord,
      previousValue: currentBank,
      newValue: updatedBank,
    });

    editorStore.updateLocalData('wordbanks', selectedBank, updatedBank);
    newWord = '';
    announceToScreenReader(`Added word: ${trimmedWord}`, 'polite');
  }

  function deleteWord(index: number) {
    if (!selectedBank || !wordBank) return;

    const wordToDelete = words[index];
    const currentBank = wordBank;
    const updatedBank: WordBank = {
      ...currentBank,
      words: currentBank.words.filter((_, i) => i !== index),
    };

    undoStore.recordAction({
      type: 'delete',
      target: 'wordbank',
      targetKey: selectedBank,
      itemKey: wordToDelete,
      previousValue: currentBank,
      newValue: updatedBank,
    });

    editorStore.updateLocalData('wordbanks', selectedBank, updatedBank);
    announceToScreenReader(`Deleted word: ${wordToDelete}`, 'polite');
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      addWord();
    }
  }
</script>

<div class="editor-container">
  <section aria-labelledby="wordbank-select-heading">
    <h2 id="wordbank-select-heading">Select Wordbank</h2>

    {#if $editorStore.isLoading && !overview}
      <p>Loading wordbanks...</p>
    {:else if overview}
      <select
        bind:value={selectedBank}
        on:change={() => selectedBank && selectBank(selectedBank)}
        aria-label="Choose wordbank to edit"
        class="wordbank-select"
      >
        <option value={null}>-- Select a wordbank --</option>
        {#each overview.wordbanks as bank}
          <option value={bank}>{bank}</option>
        {/each}
      </select>
    {:else}
      <p class="error-text">Failed to load wordbanks</p>
    {/if}
  </section>

  {#if selectedBank && wordBank}
    <section aria-labelledby="words-heading">
      <h2 id="words-heading">
        Words ({words.length})
        <span class="metadata">
          Max per slot: {wordBank.maxPerSlot} |
          Shown: {wordBank.numWordsShown}
        </span>
      </h2>

      {#if words.length === 0}
        <p class="empty-message">No words in this wordbank. Add one below!</p>
      {:else}
        <ul class="word-list" role="list">
          {#each words as word, i}
            <li class="word-item">
              <span class="word-text">{word}</span>
              <button
                on:click={() => deleteWord(i)}
                aria-label="Delete word: {word}"
                class="delete-button"
              >
                Delete
              </button>
            </li>
          {/each}
        </ul>
      {/if}

      <div class="add-word-section">
        <h3>Add New Word</h3>
        <div class="add-word-form">
          <label for="new-word" class="visually-hidden">New word</label>
          <input
            id="new-word"
            type="text"
            bind:value={newWord}
            on:keydown={handleKeydown}
            placeholder="Enter a new word..."
            aria-label="New word to add"
          />
          <button
            on:click={addWord}
            disabled={!newWord.trim()}
            aria-label="Add word"
            class="add-button"
          >
            Add Word
          </button>
        </div>
      </div>
    </section>
  {/if}
</div>

<style>
  .editor-container {
    max-width: 900px;
    margin: 0 auto;
    padding: 2rem;
  }

  section {
    margin-bottom: 2rem;
  }

  h2 {
    margin-top: 0;
    margin-bottom: 1rem;
    color: var(--color-text-primary, #333);
  }

  h3 {
    margin-top: 0;
    margin-bottom: 0.75rem;
    font-size: 1.125rem;
    color: var(--color-text-primary, #333);
  }

  .metadata {
    font-size: 0.875rem;
    font-weight: normal;
    color: var(--color-text-secondary, #666);
  }

  .wordbank-select {
    width: 100%;
    max-width: 400px;
    padding: 0.75rem;
    font-size: 1rem;
    border: 2px solid var(--color-border, #ddd);
    border-radius: 4px;
    background: white;
  }

  .wordbank-select:focus {
    outline: 3px solid var(--color-focus, #007bff);
    outline-offset: 2px;
  }

  .word-list {
    list-style: none;
    padding: 0;
    margin: 0 0 2rem 0;
  }

  .word-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    background: white;
    border: 1px solid var(--color-border, #ddd);
    margin-bottom: 0.5rem;
    border-radius: 4px;
    transition: background 0.2s;
  }

  .word-item:hover {
    background: var(--color-bg-hover, #f8f9fa);
  }

  .word-text {
    flex: 1;
    font-size: 1rem;
    color: var(--color-text-primary, #333);
  }

  .delete-button {
    background: var(--color-danger, #dc3545);
    color: white;
    border: none;
    padding: 0.4rem 0.8rem;
    border-radius: 4px;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background 0.2s;
  }

  .delete-button:hover {
    background: var(--color-danger-hover, #c82333);
  }

  .delete-button:focus {
    outline: 3px solid var(--color-focus, #007bff);
    outline-offset: 2px;
  }

  .add-word-section {
    background: var(--color-bg-secondary, #f8f9fa);
    padding: 1.5rem;
    border-radius: 8px;
    border: 2px dashed var(--color-border, #ddd);
  }

  .add-word-form {
    display: flex;
    gap: 0.75rem;
  }

  input[type="text"] {
    flex: 1;
    padding: 0.75rem;
    font-size: 1rem;
    border: 2px solid var(--color-border, #ddd);
    border-radius: 4px;
  }

  input[type="text"]:focus {
    outline: 3px solid var(--color-focus, #007bff);
    outline-offset: 2px;
  }

  .add-button {
    background: var(--color-success, #28a745);
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 4px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
  }

  .add-button:hover:not(:disabled) {
    background: var(--color-success-hover, #218838);
  }

  .add-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .add-button:focus {
    outline: 3px solid var(--color-focus, #007bff);
    outline-offset: 2px;
  }

  .empty-message {
    padding: 2rem;
    text-align: center;
    color: var(--color-text-secondary, #666);
    background: var(--color-bg-secondary, #f8f9fa);
    border-radius: 8px;
  }

  .error-text {
    color: var(--color-danger, #dc3545);
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
</style>
